// src/components/CyberpunkBackground.js
// ============================================================================
// CYBERPUNK BACKGROUND
//
// Full-screen animated backdrop used on the landing page. Stacks five layers
// to give the page a premium eSport / cyberpunk feel without requiring any
// runtime JS animation work (everything is GPU-accelerated CSS):
//
//   1. Drifting radial-gradient mesh in brand colors (gold/cyan/magenta)
//   2. Perspective grid floor scrolling toward the viewer (Tron-style)
//   3. Gold King Crit d20 dice spinning downward through the scene
//   4. Subtle CRT scanlines for tech texture
//   5. Light vignette to keep edges from being totally flat
//
// Web-only — native gets a flat #000 fallback (the site is web-first today).
// All layers are pointerEvents="none" so they never intercept clicks.
// ============================================================================

import React, { useEffect } from 'react';
import { View, Platform, StyleSheet } from 'react-native';

const COLORS = {
  GOLD: '#FCD000',
  CYAN: '#3aafff',
  MAGENTA: '#ff2eb0',
};

// Falling-dice assets (transparent webp, ~12KB each). require() resolves to a
// URL string under webpack (web); we normalize in case the loader wraps it in
// an object. Native uses the flat-black fallback and never renders these.
const _resolveSrc = (m) => (typeof m === 'string' ? m : (m && (m.uri || m.default))) || '';
const DIE_1 = _resolveSrc(require('../../assets/1_dice.webp'));
const DIE_20 = _resolveSrc(require('../../assets/20_dice.webp'));

// One-time keyframe injection. We append a <style> tag the first time any
// instance mounts and never remove it — the cost is a few hundred bytes of
// CSS and the rules can stay live for the whole session.
let stylesInjected = false;
const injectKeyframes = () => {
  if (stylesInjected || Platform.OS !== 'web' || typeof document === 'undefined') return;
  stylesInjected = true;

  const style = document.createElement('style');
  style.setAttribute('data-critcash', 'cyberpunk-bg');
  style.textContent = `
    @keyframes critcash-mesh-drift {
      0%   { background-position: 0% 0%, 100% 100%, 50% 50%, 50% 0%, 0% 100%; }
      50%  { background-position: 10% 15%, 90% 85%, 55% 45%, 45% 5%, 5% 95%; }
      100% { background-position: 0% 0%, 100% 100%, 50% 50%, 50% 0%, 0% 100%; }
    }
    @keyframes critcash-grid-scroll {
      0%   { transform: perspective(600px) rotateX(60deg) translateY(0); }
      100% { transform: perspective(600px) rotateX(60deg) translateY(60px); }
    }
    /* Dice fall — a d20 spins as it descends from above the viewport to
       below. Per-die spin amount/direction via --spin and hold opacity via
       --max-op so the field doesn't read as a uniform loop. */
    @keyframes critcash-dice-fall {
      0%   { transform: translate3d(0, -20vh, 0) rotate(0deg); opacity: 0; }
      8%   { opacity: var(--max-op, 0.85); }
      92%  { opacity: var(--max-op, 0.85); }
      100% { transform: translate3d(0, 120vh, 0) rotate(var(--spin, 540deg)); opacity: 0; }
    }
    @keyframes critcash-scanline-drift {
      0%   { background-position: 0 0; }
      100% { background-position: 0 6px; }
    }
    @media (prefers-reduced-motion: reduce) {
      .critcash-cyber-bg * {
        animation: none !important;
      }
    }
  `;
  document.head.appendChild(style);
};

// Nine gold d20 dice tumbling downward across the viewport — the brand's King
// Crit dice raining through cyberspace (replaces the old wireframe cubes).
// Alternates the "1" and "20" faces, spread laterally so they don't clump,
// varied in size for depth, varied spin amount/direction per die.
const DICE = Array.from({ length: 9 }, (_, i) => ({
  src: i % 2 === 0 ? DIE_1 : DIE_20,
  left: `${(i * 10.7 + 4) % 96}%`,           // 4% -> 90% spread
  size: 30 + ((i * 13) % 36),                // 30 - 66px
  duration: 12 + ((i * 1.7) % 11),           // 12 - 23s
  delay: -(i * 2.3),                         // pre-staggered so first paint has dice mid-fall
  maxOpacity: 0.45 + ((i * 13) % 8) / 25,    // ~0.45 - 0.73
  spin: (i % 3 === 0 ? -1 : 1) * (360 + ((i * 77) % 360)), // mixed CW/CCW, 360-720deg
}));

// One die = a single transparent webp <img> that falls + spins. Gold drop-
// shadow gives it a soft brand glow against the dark scene.
function Die({ src, size, left, duration, delay, maxOpacity, spin }) {
  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: 0,
        left,
        width: `${size}px`,
        height: `${size}px`,
        objectFit: 'contain',
        animation: `critcash-dice-fall ${duration}s linear ${delay}s infinite`,
        willChange: 'transform, opacity',
        filter: `drop-shadow(0 0 ${Math.round(size * 0.3)}px rgba(252, 208, 0, 0.4))`,
        // @ts-ignore — custom properties consumed inside the keyframe
        '--max-op': maxOpacity,
        '--spin': `${spin}deg`,
      }}
    />
  );
}

export default function CyberpunkBackground() {
  useEffect(() => {
    injectKeyframes();
  }, []);

  // Native fallback — flat black.
  if (Platform.OS !== 'web') {
    return <View pointerEvents="none" style={styles.nativeFallback} />;
  }

  return (
    <View
      pointerEvents="none"
      style={styles.root}
      // className lets the prefers-reduced-motion media query disable
      // animations for accessibility-sensitive users.
      // @ts-ignore — RN Web passes className through
      className="critcash-cyber-bg"
    >
      {/* Layer 1: drifting brand-color mesh — five overlapping radial
          gradients in gold/cyan/magenta that slowly reposition over 38s.
          Provides the constant ambient color wash through every section
          of the page. */}
      <div style={meshStyle} />

      {/* Layer 2: perspective grid floor — Tron-style cyan grid lines
          scrolling toward the viewer. Masked to fade out toward the
          horizon so it doesn't form a hard top edge. */}
      <div style={gridWrapperStyle}>
        <div style={gridFloorStyle} />
      </div>

      {/* Layer 3: falling King Crit dice — nine gold d20s of varied size and
          speed spin downward across the viewport, raining the brand's dice
          through the cyberpunk scene (replaces the old wireframe cubes). */}
      <div style={diceFieldStyle}>
        {DICE.map((d, i) => (
          <Die key={i} {...d} />
        ))}
      </div>

      {/* Layer 4: CRT scanlines — repeating 3px stripe at very low alpha,
          slow drift. Constant subliminal "screen" texture. */}
      <div style={scanlineStyle} />

      {/* Layer 5: light vignette. Softened — just enough to keep the
          four corners from being totally flat. */}
      <div style={vignetteStyle} />
    </View>
  );
}

// ----------------------------------------------------------------------------
// Layer styles
//
// Plain JS objects passed to <div style={...}> rather than StyleSheet
// entries — RN Web's style validator rejects CSS shorthands like
// `background` and custom properties, so going through raw DOM divs
// keeps the styles intact.
// ----------------------------------------------------------------------------

const meshStyle = {
  position: 'absolute',
  inset: 0,
  background: `
    radial-gradient(ellipse 90% 70% at 15% 18%, rgba(252, 208, 0, 0.20), transparent 65%),
    radial-gradient(ellipse 80% 60% at 88% 78%, rgba(58, 175, 255, 0.20), transparent 65%),
    radial-gradient(ellipse 70% 50% at 50% 50%, rgba(255, 46, 176, 0.10), transparent 70%),
    radial-gradient(ellipse 100% 80% at 50% 0%, rgba(252, 208, 0, 0.09), transparent 70%),
    radial-gradient(ellipse 60% 50% at 10% 90%, rgba(58, 175, 255, 0.12), transparent 70%),
    #000
  `,
  backgroundSize: '130% 130%, 130% 130%, 140% 140%, 130% 130%, 120% 120%, 100% 100%',
  animation: 'critcash-mesh-drift 38s ease-in-out infinite',
};

const gridWrapperStyle = {
  position: 'absolute',
  left: '-50%',
  right: '-50%',
  bottom: '-20%',
  height: '70%',
  WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 40%, transparent 100%)',
  maskImage: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 40%, transparent 100%)',
  opacity: 0.35,
  pointerEvents: 'none',
  overflow: 'hidden',
};

const gridFloorStyle = {
  position: 'absolute',
  inset: '-50%',
  backgroundImage: `
    linear-gradient(to right, rgba(58, 175, 255, 0.5) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(58, 175, 255, 0.5) 1px, transparent 1px)
  `,
  backgroundSize: '60px 60px',
  animation: 'critcash-grid-scroll 6s linear infinite',
  transformOrigin: 'center bottom',
};

// The dice field just clips the falling dice to the viewport. (The old cube
// field needed a 3D perspective; the dice spin in 2D so none is required.)
const diceFieldStyle = {
  position: 'absolute',
  inset: 0,
  overflow: 'hidden',
};

const scanlineStyle = {
  position: 'absolute',
  inset: 0,
  background: 'repeating-linear-gradient(to bottom, rgba(255, 255, 255, 0.018) 0px, rgba(255, 255, 255, 0.018) 1px, transparent 1px, transparent 3px)',
  animation: 'critcash-scanline-drift 8s linear infinite',
  pointerEvents: 'none',
  mixBlendMode: 'overlay',
};

const vignetteStyle = {
  position: 'absolute',
  inset: 0,
  background: 'radial-gradient(ellipse at center, transparent 60%, rgba(0, 0, 0, 0.30) 100%)',
  pointerEvents: 'none',
};

const styles = StyleSheet.create({
  root: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    overflow: 'hidden',
    backgroundColor: '#000',
  },
  nativeFallback: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    backgroundColor: '#000',
  },
});
